import React, { useState, useEffect, useContext } from 'react';
import { Field } from 'react-final-form';
import clsx from 'clsx';
import Select from 'react-select-5';
import BuilderContext from '@/components/common/Context';
import I18n from '@/modules/i18n';
import { compareGlossaryTerms } from '@/utils/sortGlossaryTerms';
import useVocabularyStore from '../../useVocabularyStore';

interface VocabDropdownProps {
  id: string;
  displayPosition: number;
  position: number;
  setUpdatedChoices: (choices: any) => void;
  selectedChoices?: any[];
  updatedChoices?: any[];
  vocabId?: number;
  vocabLinkOption: string;
}

const SelectComponent = ({ input, ...rest }) => (
  <Select
    {...input}
    {...rest}
    value={rest.selectValue}
  />
);

const VocabDropdown = ({
  id, displayPosition, position, setUpdatedChoices, selectedChoices, vocabId, vocabLinkOption,
}: VocabDropdownProps) => {
  const rawVocabularyTerms = useVocabularyStore(state => state.vocabularyTerms);
  const vocabularyTerms = rawVocabularyTerms
    .filter(term => term.kind !== 'Academic')
    .sort((a, b) => compareGlossaryTerms(a.term, b.term, I18n.locale));
  const setSelectedTerm = useVocabularyStore(state => state.setSelectedTerm);

  const {
    dropZones,
    draggableChoices,
    textAreas,
    dropZoneDispatch,
    draggableChoiceDispatch,
    textAreaDispatch,
  } = useContext(BuilderContext);

  const getInitialVocab = () => {
    const vocabTerm = vocabularyTerms.find(vocab => vocab.value === vocabId);
    if (vocabTerm) {
      return {
        definition: vocabTerm.definition,
        image: vocabTerm.image,
        kind: vocabTerm.kind,
        label: vocabTerm.label,
        term: vocabTerm.term,
        value: vocabTerm.value,
        vocabDefinition: vocabTerm.vocabDefinition,
        vocabTerm: vocabTerm.vocabTerm,
      };
    }
    return { label: '', value: '', vocabTerm: '' };
  };

  const [selectedVocab, setSelectedVocab] = useState(getInitialVocab());

  const updatedChoices = (prevState, removing = false) => {
    if (removing) return prevState.filter(choice => choice.id !== id);

    const noVocabSelected = selectedVocab.value === '';

    // If no vocab is selected and the current dropdown doesn't have an associated vocabId, skip it in the update.
    if (noVocabSelected && !vocabId) return prevState;

    const filteredChoices = prevState.filter(choice => choice.id !== id);

    return [
      ...filteredChoices,
      {
        id,
        title: noVocabSelected ? `Draggable Choice ${id}` : selectedVocab.vocabTerm,
        type: noVocabSelected ? 'draggableChoice' : 'vocab',
        vocabId: noVocabSelected ? null : selectedVocab.value,
      },
    ];
  };

  const deleteVocabTerm = () => {
    const dropZonesToDelete = Object.keys(dropZones).filter(key => dropZones[key].vocabId === vocabId);
    const draggableChoicesToDelete = Object.keys(draggableChoices).filter((key) => {
      if (vocabId) return draggableChoices[key].vocabId === vocabId;

      return draggableChoices[key].position === position;
    });
    const textAreasToDelete = Object.keys(textAreas).filter(key => textAreas[key].vocabId === vocabId);

    textAreasToDelete.forEach(key => textAreaDispatch({ payload: { id: key }, type: 'DELETE' }));
    draggableChoicesToDelete.forEach(key => draggableChoiceDispatch({ payload: { id: key }, type: 'DELETE' }));
    dropZonesToDelete.forEach(key => dropZoneDispatch({ payload: { id: key }, type: 'DELETE' }));

    setUpdatedChoices(updatedChoices(selectedChoices, true));
  };

  useEffect(() => {
    setUpdatedChoices(updatedChoices);
    setSelectedTerm(id, selectedVocab.value);
  }, [selectedVocab]);

  const dropdownOptions = () => {
    if (vocabLinkOption === 'definition') {
      return vocabularyTerms.map(term => ({ ...term }));
    }

    // Vocab terms without images default to a placeholder image called "/missing.png".
    return vocabularyTerms.filter(term => term.image && term.image !== '/missing.png').map(term => ({ ...term }));
  };

  const handleChange = (e) => {
    if (e && e.value) {
      setSelectedVocab(e);
    } else {
      setSelectedVocab({ label: '', value: '', vocabTerm: '' });
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-mt-3">
      <div>Answer Choice {displayPosition}</div>
      &nbsp;
      <button
        className={clsx(
          'fa fa-trash-o tw-text-red tw-border-0 tw-bg-transparent tw-w-[24px] tw-h-[24px] tw-cursor-pointer',
          { 'tw-invisible': id === 'a' },
        )}
        type="button"
        onClick={deleteVocabTerm}
      />
      <Field
        isClearable
        className="tw-ml-3 tw-w-3/4"
        component={SelectComponent}
        filterOption={(vocabOption, searchInput) => {
          const inputValue = searchInput.toLowerCase();
          return vocabOption.data.term.toLowerCase().includes(inputValue);
        }}
        formatOptionLabel={(vocabOption) => {
          if (vocabLinkOption === 'definition') {
            return (
              <div>{vocabOption.label}</div>
            );
          }

          return (
            <div className="tw-flex tw-items-center">
              <img alt={vocabOption.term} className="tw-w-[4rem] tw-mr-2" src={vocabOption.image} />
              <div><strong>{vocabOption.term}</strong></div>
            </div>
          );
        }}
        isOptionDisabled={option => selectedChoices.some(choice => choice.vocabId === option.value)}
        name="vocabulary_dropdown"
        options={dropdownOptions()}
        selectValue={selectedVocab}
        onChange={handleChange}
      />
    </div>
  );
};

export default VocabDropdown;
